// Plan 08-06 — Pre-canned tutorial script (DEC-021 exception).
//
// Deterministic 6-step Apprentice match driving the player through the core
// loop in ~5 minutes (30s/turn budget × 6 turns). Runes are drawn against
// TUTORIAL_DECK_SEED so the seed pool is reproducible across reloads.
//
// Runes in this script are chosen to make CAT, BAT, and TAB all winnable
// from the same seed pool ['C','A','T','B','I','N'] (the player traces
// purely from their seeded row in T1; T6 reveals shared cells that aren't
// strictly needed). Plan 30-04 cleanup renamed the legacy per-player-rack
// terminology to "seed runes" to match the Runespring unified-field model.

import type { TutorialStep } from './types'

/** Deterministic deck seed — pinned in Task 1 behavior bullet. */
export const TUTORIAL_DECK_SEED = 'tutorial-court-of-runes-v1' as const

/**
 * Initial runes seeded on the player's row (slot 0). Picked to enable the
 * canonical "trace CAT" hint at T1 without needing a shared-cell letter.
 */
export const TUTORIAL_SEED_RUNES: ReadonlyArray<string> = Object.freeze([
  'C',
  'A',
  'T',
  'B',
  'I',
  'N',
])

/**
 * Apprentice's seed row — used purely so coach copy can reference an
 * Apprentice "BAT" word that resolves into the token bank visibly.
 */
export const APPRENTICE_SEED_RUNES: ReadonlyArray<string> = Object.freeze([
  'B',
  'A',
  'T',
  'O',
  'R',
  'E',
])

/**
 * The deterministic script. Each step is processed by `runner.advance()`.
 *
 * Steps (max turn_idx = 6):
 *  T1 reveal       — READY cinematic; deal letters
 *  T1 player turn  — coach: "Drag across letters to spell" (word_trace)
 *  T2 apprentice   — Apprentice plays BAT, picks up tokens, moves a piece
 *  T3 player turn  — coach: "Tokens build here" (token_bank)
 *  T4 player turn  — coach: "Spend a token. Make a Knight." (first_empower)
 *  T6 reveal       — THE FLOP cinematic (skipped T5 — pacing)
 *  T6 player turn  — coach: "Take the King. Win the match." (king_capture)
 */
export const TUTORIAL_SCRIPT: ReadonlyArray<TutorialStep> = Object.freeze([
  {
    kind: 'reveal',
    turn_idx: 1,
    reveal: 't1',
  },
  {
    kind: 'player_turn',
    turn_idx: 1,
    player_hint_key: 'onboarding.coach.word_trace',
    spotlight_target: 'seed_runes',
    expected_player_action: 'trace_word',
  },
  {
    // AFFINITY-01 — teach the 4 secret affinity runes + the +1 payoff.
    kind: 'player_turn',
    turn_idx: 1,
    player_hint_key: 'onboarding.coach.affinity_intro',
    spotlight_target: 'affinity_strip',
    expected_player_action: 'tap_continue',
  },
  {
    kind: 'apprentice_turn',
    turn_idx: 2,
    apprentice_action: {
      phase_a: { kind: 'word', word: 'BAT', cells: [12, 13, 14] },
      phase_b: { kind: 'move', from: 32, to: 26 },
    },
  },
  {
    kind: 'player_turn',
    turn_idx: 3,
    player_hint_key: 'onboarding.coach.token_bank',
    spotlight_target: 'token_bank',
    expected_player_action: 'tap_continue',
  },
  {
    // ANCHOR-03 — teach Anchor placement + the Hollow trade-off.
    kind: 'player_turn',
    turn_idx: 3,
    player_hint_key: 'onboarding.coach.anchor_intro',
    spotlight_target: 'anchor_cell',
    expected_player_action: 'place_anchor',
  },
  {
    kind: 'player_turn',
    turn_idx: 4,
    player_hint_key: 'onboarding.coach.first_empower',
    spotlight_target: 'empower_button',
    expected_player_action: 'empower',
  },
  {
    // POWERUP-01 — introduce the 4-power-up loadout (some single-use, some twice per match).
    kind: 'player_turn',
    turn_idx: 5,
    player_hint_key: 'onboarding.coach.powerup_intro',
    spotlight_target: 'powerup_loadout',
    expected_player_action: 'inspect_powerups',
  },
  {
    // TUT-07 — the FLOP reveal now carries the (previously dead) t6_ready
    // signpost so a player knows six new runes are about to fall.
    kind: 'reveal',
    turn_idx: 6,
    reveal: 't6',
    player_hint_key: 'onboarding.coach.t6_ready',
    spotlight_target: 'reveal_word',
  },
  {
    kind: 'player_turn',
    turn_idx: 6,
    player_hint_key: 'onboarding.coach.king_capture',
    spotlight_target: 'opponent_king',
    expected_player_action: 'capture',
  },
])

/**
 * Static set of hint keys consumed by the tutorial. Plan 07 (i18n) reads this
 * to verify every key has en + sv translations before merging the locale
 * dictionaries.
 */
export const TUTORIAL_HINT_KEYS: ReadonlyArray<string> = Object.freeze([
  'onboarding.coach.word_trace',
  'onboarding.coach.affinity_intro',
  'onboarding.coach.token_bank',
  'onboarding.coach.anchor_intro',
  'onboarding.coach.first_empower',
  'onboarding.coach.powerup_intro',
  'onboarding.coach.t6_ready',
  'onboarding.coach.king_capture',
  'onboarding.coach.skip',
])
